// Typed client for the /products/styles endpoints used by the clothing browser.
// REACT_APP_API_BASE lets `npm start` (no Netlify functions) talk to the live API.

import { SortKey } from '../data/clothing/browseConfig';

const API_BASE = `${process.env.REACT_APP_API_BASE || ''}/.netlify/functions/products`;

export interface ColourVariant {
  rgb: string | null; // "255 239 178" or "52 56 67|184 184 184" for two-tone
  colourCode: string;
  colourName: string;
  colourImage: string | null;
  primaryImage: string | null;
}

export interface StyleSummary {
  id: string;
  style_code: string;
  style_name: string;
  brand: string;
  product_type: string;
  gender: string | null;
  age_group: string | null;
  fabric: string | null;
  size_range: string | null;
  primary_product_image_url: string | null;
  retail_description: string | null;
  specification: string | null;
  accreditations: string | null;
  sustainable_organic: string | null;
  price_min: string | null;
  price_max: string | null;
  available_sizes: string[] | null;
  available_colors: string[] | null;
  color_shades: string[] | null;
  color_variants: ColourVariant[] | null;
  variant_count: number;
}

export interface BrowseFilters {
  types: string[];
  brands: string[];
  genders: string[];
  ages: string[];
  colours: string[];
  sizes: string[];
  min?: number;
  max?: number;
  eco: boolean;
  q: string;
  sort: SortKey;
  page: number;
}

export interface FacetRow {
  value: string;
  count: number;
}

export interface Facets {
  productTypes: FacetRow[];
  brands: FacetRow[];
  genders: FacetRow[];
  ageGroups: FacetRow[];
  colourFamilies: FacetRow[];
  sustainable: number;
  total: number;
}

export interface StylesPage {
  styles: StyleSummary[];
  total: number;
  totalPages: number;
  currentPage: number;
}

export interface BrandMeta {
  name: string;
  logo_url: string | null;
}

export const EMPTY_FILTERS: BrowseFilters = {
  types: [],
  brands: [],
  genders: [],
  ages: [],
  colours: [],
  sizes: [],
  eco: false,
  q: '',
  sort: 'popular',
  page: 1,
};

function toQuery(f: BrowseFilters, pageSize: number): URLSearchParams {
  const p = new URLSearchParams();
  if (f.types.length) p.set('productType', f.types.join(','));
  if (f.brands.length) p.set('brand', f.brands.join(','));
  if (f.genders.length) p.set('gender', f.genders.join(','));
  if (f.ages.length) p.set('ageGroup', f.ages.join(','));
  if (f.colours.length) p.set('colourFamily', f.colours.join(','));
  if (f.sizes.length) p.set('sizes', f.sizes.join(','));
  if (f.min !== undefined) p.set('priceMin', String(f.min));
  if (f.max !== undefined) p.set('priceMax', String(f.max));
  if (f.eco) p.set('sustainable', 'true');
  if (f.q) p.set('search', f.q);
  p.set('sort', f.sort);
  p.set('limit', String(pageSize));
  p.set('offset', String((f.page - 1) * pageSize));
  return p;
}

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, { signal, headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json() as Promise<T>;
}

export async function fetchStyles(f: BrowseFilters, pageSize: number, signal?: AbortSignal): Promise<StylesPage> {
  const data = await getJson<{ success: boolean } & StylesPage>(`${API_BASE}/styles?${toQuery(f, pageSize)}`, signal);
  return {
    styles: data.styles || [],
    total: data.total || 0,
    totalPages: data.totalPages || 0,
    currentPage: data.currentPage || 1,
  };
}

export async function fetchFacets(f: BrowseFilters, signal?: AbortSignal): Promise<Facets | null> {
  try {
    const data = await getJson<{ success: boolean } & Facets>(`${API_BASE}/styles/facets?${toQuery(f, 1)}`, signal);
    return data.success ? data : null;
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    return null; // facets are an enhancement; the list still renders without counts
  }
}

export async function fetchBrandMeta(): Promise<BrandMeta[]> {
  try {
    const data = await getJson<{ success: boolean; brands: BrandMeta[] }>(`${API_BASE}/brands`);
    return data.brands || [];
  } catch {
    return [];
  }
}

// --- helpers shared by the UI -------------------------------------------

export const priceFrom = (s: StyleSummary): number | null => {
  const n = parseFloat(s.price_min || '');
  return Number.isFinite(n) ? n : null;
};

export const formatGbp = (n: number): string =>
  n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 });

/** "52 56 67|184 184 184" → CSS background (solid or split swatch). */
export const swatchFromRgb = (rgb: string | null | undefined): string => {
  if (!rgb) return '#d4d4d8';
  const parts = rgb.split('|').map((p) => {
    const [r, g, b] = p.trim().split(/\s+/).map(Number);
    return [r, g, b].every((v) => Number.isFinite(v)) ? `rgb(${r} ${g} ${b})` : null;
  }).filter(Boolean) as string[];
  if (!parts.length) return '#d4d4d8';
  if (parts.length === 1) return parts[0];
  return `linear-gradient(135deg, ${parts[0]} 0 50%, ${parts[1]} 50% 100%)`;
};

export const cleanText = (s: string | null | undefined): string =>
  (s || '').replace(/�/g, "'").trim();

const SIZE_ORDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL', '7XL', '8XL'];

/** Adult letter sizes in wearable order, everything else after in natural order. */
export const sortSizes = (sizes: string[]): string[] => {
  const rank = (s: string) => {
    const i = SIZE_ORDER.indexOf(s.toUpperCase());
    return i === -1 ? SIZE_ORDER.length : i;
  };
  return [...sizes].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b, 'en', { numeric: true }));
};

/** Brand names lost their ® in import ("TriDri�"); drop the marker for display only. */
export const cleanBrand = (s: string | null | undefined): string => (s || '').replace(/�/g, '').trim();

/** Size ranges lost their en dash in import ("XXS � 3XL"). */
export const cleanRange = (s: string | null | undefined): string =>
  (s || '').replace(/\s*�\s*/g, ' – ').trim();
